import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type {
  UserDetail,
  UserInfoResponse,
  UserListPayload,
  UserSummary
} from "@dealer-center/ddc-pages-configuration-interface";
import { CONFIGURATION_QUERY_KEY_BASE } from "./configurationRequest";

/*
 * Writes update the cache instead of refetching: CC9 reads lag its writes, so an immediate refetch
 * returns the pre-write record. The cache holds until the entries go stale or the page reloads.
 */

/**
 * Cancels in-flight reads so lagging data can't overwrite the write. Awaited because a cancelled
 * fetch reverts asynchronously; entries without data are skipped so a first load isn't stranded.
 */
export const cancelInFlightReads = (
  queryClient: QueryClient,
  queryKey: QueryKey
) =>
  queryClient.cancelQueries({
    queryKey,
    predicate: query => query.state.data !== undefined
  });

const listPrefix = (accountId: string) => [
  CONFIGURATION_QUERY_KEY_BASE,
  "userlist",
  accountId
];

/** `useGetUserList`'s key: `[BASE, "userlist", accountId, filter, sortKey, sortDir, page, size]`. */
const FILTER_INDEX_IN_KEY = 3;
const PAGE_INDEX_IN_KEY = 6;
const SIZE_INDEX_IN_KEY = 7;

// Updaters return `undefined` for no change, so React Query skips the write and keeps the stale clock.

const pageCount = (queryKey: QueryKey, totalCount: number) => {
  const size = queryKey[SIZE_INDEX_IN_KEY];
  return typeof size === "number" && size > 0
    ? Math.ceil(totalCount / size)
    : undefined;
};

/** Keeps `totalPages` and the page's length consistent with a changed `totalCount`. */
const withCount = (
  queryKey: QueryKey,
  current: UserListPayload,
  users: UserListPayload["users"],
  totalCount: number
): UserListPayload => {
  const size = queryKey[SIZE_INDEX_IN_KEY];
  return {
    ...current,
    users: typeof size === "number" && size > 0 ? users.slice(0, size) : users,
    totalCount,
    totalPages: pageCount(queryKey, totalCount) ?? current.totalPages
  };
};

/** Whether a filtered list would include the user is the server's call, so those entries aren't guessed at. */
const isFiltered = (queryKey: QueryKey) => {
  const filter = queryKey[FILTER_INDEX_IN_KEY];
  return typeof filter === "string" && filter.trim() !== "";
};

type UserSummaryEditable = Partial<
  Pick<UserSummary, "firstName" | "lastName" | "email">
>;

export const mergeUserIntoListCache = async (
  queryClient: QueryClient,
  accountId: string,
  userId: string,
  fields: UserSummaryEditable
) => {
  const changes = (
    ["firstName", "lastName", "email"] as const
  ).reduce<UserSummaryEditable>(
    (found, key) =>
      fields[key] === undefined ? found : { ...found, [key]: fields[key] },
    {}
  );
  if (Object.keys(changes).length === 0) {
    return;
  }

  await cancelInFlightReads(queryClient, listPrefix(accountId));
  queryClient.setQueriesData<UserListPayload | undefined>(
    { queryKey: listPrefix(accountId) },
    current =>
      current?.users.some(user => user.userId === userId)
        ? {
            ...current,
            users: current.users.map(user =>
              user.userId === userId ? { ...user, ...changes } : user
            )
          }
        : undefined
  );
};

/** Adds the user to page 0 of each unfiltered cached list; other pages only gain a count. */
export const addUserToListCache = async (
  queryClient: QueryClient,
  accountId: string,
  user: UserSummary
) => {
  await cancelInFlightReads(queryClient, listPrefix(accountId));
  queryClient
    .getQueryCache()
    .findAll({ queryKey: listPrefix(accountId) })
    .filter(query => !isFiltered(query.queryKey))
    .forEach(query => {
      queryClient.setQueryData<UserListPayload | undefined>(
        query.queryKey,
        current => {
          if (
            !current ||
            current.users.some(row => row.userId === user.userId)
          ) {
            return undefined;
          }

          const isFirstPage = query.queryKey[PAGE_INDEX_IN_KEY] === 0;

          return withCount(
            query.queryKey,
            current,
            isFirstPage ? [user, ...current.users] : current.users,
            current.totalCount + 1
          );
        }
      );
    });
};

/** Removes the row wherever it's cached; unfiltered pages without it still lose a count. */
export const removeUserFromListCache = async (
  queryClient: QueryClient,
  accountId: string,
  userId: string
) => {
  await cancelInFlightReads(queryClient, listPrefix(accountId));
  queryClient
    .getQueryCache()
    .findAll({ queryKey: listPrefix(accountId) })
    .forEach(query => {
      queryClient.setQueryData<UserListPayload | undefined>(
        query.queryKey,
        current => {
          const hasRow = current?.users.some(row => row.userId === userId);
          if (!current || (!hasRow && isFiltered(query.queryKey))) {
            return undefined;
          }

          return withCount(
            query.queryKey,
            current,
            current.users.filter(row => row.userId !== userId),
            Math.max(0, current.totalCount - 1)
          );
        }
      );
    });
};

/**
 * Seeds the new user's profile so the detail panel doesn't read a 404. Dealership fields copied
 * on-prem (`useDealershipInfo`) appear on the next refetch.
 */
export const seedCreatedUserInfo = (
  queryClient: QueryClient,
  accountId: string,
  user: Pick<UserDetail, "userId" | "firstName" | "lastName">
) => {
  const key = [
    CONFIGURATION_QUERY_KEY_BASE,
    "userinfo",
    accountId,
    user.userId
  ];
  if (queryClient.getQueryData(key)) {
    return;
  }

  const summary: UserDetail = {
    userId: user.userId,
    platformId: null,
    title: "",
    firstName: user.firstName,
    lastName: user.lastName,
    parentId: accountId,
    ownerId: accountId,
    email: "",
    email2: "",
    homePhone: "",
    workPhone: "",
    mobile: "",
    fax: "",
    company: "",
    created: Date.now()
  };

  queryClient.setQueryData<UserInfoResponse>(key, { summary });
};
